import React, { useState } from 'react';

/**
 * HVACCalculators Component
 *
 * Professional HVAC calculation tools for technicians in the field
 * - Airflow CFM Calculator
 * - Subcool Calculator
 * - Superheat Calculator
 * - Duct Sizing Calculator
 */

const HVACCalculators = ({ onCalculation }) => {
  const [activeCalculator, setActiveCalculator] = useState(null);
  const [results, setResults] = useState({});
  const [recentCalculations, setRecentCalculations] = useState([]);
  const [copyToast, setCopyToast] = useState(null);

  // Toast notification helper
  const showCopyToast = (message) => {
    setCopyToast(message);
    setTimeout(() => setCopyToast(null), 2000);
  };

  // Copy to clipboard helper
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      showCopyToast(`✓ Copied ${label}`);
    }).catch(() => {
      showCopyToast('✗ Copy failed');
    });
  };

  // Airflow CFM Calculator - Supports both HVAC formulas
  const calculateAirflowCFM = (inputs) => {
    const { tons, btu, tempRise } = inputs;

    let cfmByTonnage = null;
    let cfmByBTU = null;
    let percentDiff = null;
    let comparisonMessage = '';

    // Method 1: Tonnage-based (400 CFM per ton standard)
    if (tons && tons > 0) {
      const cfmPerTon = 400;
      cfmByTonnage = tons * cfmPerTon;
    }

    // Method 2: BTU and temperature difference
    if (btu && btu > 0 && tempRise && tempRise > 0) {
      cfmByBTU = btu / (1.08 * tempRise);
    }

    // Calculate percentage difference and comparison message
    if (cfmByTonnage && cfmByBTU) {
      const diff = Math.abs(cfmByTonnage - cfmByBTU);
      percentDiff = Math.round((diff / cfmByTonnage) * 100);

      if (percentDiff <= 5) {
        comparisonMessage = '✅ Both methods agree - airflow looks normal';
      } else if (percentDiff <= 10) {
        comparisonMessage = '⚠️ Slightly different - verify system conditions';
      } else {
        comparisonMessage = '🚨 Significant difference - check for system issues';
      }
    }

    // Single method recommendation
    const primaryCFM = cfmByTonnage || cfmByBTU;
    let recommendation = '';
    if (primaryCFM) {
      const cfmPerTon = primaryCFM / (tons || (btu / 12000));
      recommendation = cfmPerTon >= 350 && cfmPerTon <= 450
        ? '✅ Airflow is within acceptable range (350-450 CFM/ton)'
        : cfmPerTon < 350
          ? '⚠️ Airflow may be too low - check for restrictions'
          : '⚠️ Airflow may be too high - verify system sizing';
    }

    return {
      cfmByTonnage: cfmByTonnage ? Math.round(cfmByTonnage) : null,
      cfmByBTU: cfmByBTU ? Math.round(cfmByBTU) : null,
      percentDiff,
      comparisonMessage,
      recommendation,
      tons,
      btu,
      tempRise
    };
  };

  // Subcool Calculator
  const calculateSubcool = (inputs) => {
    const { liquidLineTemp, condensingTemp } = inputs;

    // Subcooling = Condensing Temperature - Liquid Line Temperature
    const subcool = condensingTemp - liquidLineTemp;

    let status = '';
    let recommendation = '';

    if (subcool < 5) {
      status = '⚠️ LOW SUBCOOL';
      recommendation = 'Possible undercharge or non-condensables. Check for leaks or purge system.';
    } else if (subcool >= 5 && subcool <= 15) {
      status = '✅ NORMAL SUBCOOL';
      recommendation = 'Refrigerant charge is optimal.';
    } else {
      status = '⚠️ HIGH SUBCOOL';
      recommendation = 'Possible overcharge or restricted liquid line. Check TXV and filter drier.';
    }

    return {
      subcool: Math.round(subcool * 10) / 10, // Round to 1 decimal
      status,
      recommendation,
      liquidLineTemp,
      condensingTemp
    };
  };

  // Superheat Calculator
  const calculateSuperheat = (inputs) => {
    const { suctionLineTemp, evaporatingTemp } = inputs;

    // Superheat = Suction Line Temperature - Evaporating Temperature
    const superheat = suctionLineTemp - evaporatingTemp;

    let status = '';
    let recommendation = '';

    if (superheat < 5) {
      status = '⚠️ LOW SUPERHEAT';
      recommendation = 'Possible overcharge or TXV stuck open. Risk of liquid flood-back.';
    } else if (superheat >= 5 && superheat <= 15) {
      status = '✅ NORMAL SUPERHEAT';
      recommendation = 'Refrigerant charge is optimal.';
    } else {
      status = '⚠️ HIGH SUPERHEAT';
      recommendation = 'Possible undercharge or TXV stuck closed. Check for leaks.';
    }

    return {
      superheat: Math.round(superheat * 10) / 10,
      status,
      recommendation,
      suctionLineTemp,
      evaporatingTemp
    };
  };

  // Duct Sizing Calculator
  const calculateDuctSize = (inputs) => {
    const { cfm, velocity } = inputs;

    // Cross-sectional area (sq ft) = CFM / Velocity (fpm)
    const area = cfm / velocity;

    // Convert to square inches
    const areaInches = area * 144;

    // Calculate diameter for round ducts (inches)
    const diameter = Math.sqrt((4 * areaInches) / Math.PI);

    // Calculate rectangular duct dimensions (common aspect ratios)
    const width = Math.sqrt(areaInches * 1.5); // 1.5:1 aspect ratio
    const height = areaInches / width;

    let velocityStatus = '';
    if (velocity < 600) {
      velocityStatus = '⚠️ Low velocity - may cause noise issues';
    } else if (velocity >= 600 && velocity <= 900) {
      velocityStatus = '✅ Optimal velocity range';
    } else {
      velocityStatus = '⚠️ High velocity - may cause excessive noise';
    }

    return {
      roundDiameter: Math.round(diameter * 10) / 10,
      rectangularWidth: Math.round(width),
      rectangularHeight: Math.round(height),
      area: Math.round(area * 100) / 100,
      velocityStatus,
      cfm,
      velocity
    };
  };

  const handleCalculate = (calculatorType, inputs) => {
    let result = null;

    switch (calculatorType) {
      case 'airflow':
        result = calculateAirflowCFM(inputs);
        break;
      case 'subcool':
        result = calculateSubcool(inputs);
        break;
      case 'superheat':
        result = calculateSuperheat(inputs);
        break;
      case 'ductSize':
        result = calculateDuctSize(inputs);
        break;
      default:
        return;
    }

    setResults({ ...results, [calculatorType]: result });

    // Add to recent calculations history
    const calculationRecord = {
      type: calculatorType,
      timestamp: new Date().toLocaleTimeString(),
      inputs,
      result,
      id: Date.now()
    };
    setRecentCalculations([calculationRecord, ...recentCalculations].slice(0, 5));

    // Enhanced telemetry callback to parent component
    if (onCalculation) {
      onCalculation({
        type: calculatorType,
        inputs,
        result,
        timestamp: new Date().toISOString(),
        sessionContext: {
          calculationCount: recentCalculations.length + 1,
          mostUsedCalculator: getMostUsedCalculator()
        }
      });
    }
  };

  // Helper to track which calculator is used most
  const getMostUsedCalculator = () => {
    const counts = recentCalculations.reduce((acc, calc) => {
      acc[calc.type] = (acc[calc.type] || 0) + 1;
      return acc;
    }, {});
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || null;
  };

  // Helper to get calculator label
  const getCalculatorLabel = (type) => {
    const labels = {
      airflow: '💨 Airflow',
      subcool: '❄️ Subcool',
      superheat: '🔥 Superheat',
      ductSize: '📐 Duct Size'
    };
    return labels[type] || type;
  };

  // Helper to get calculation summary
  const getCalculatorSummary = (calc) => {
    const { type, result } = calc;
    switch (type) {
      case 'airflow':
        return `${result.cfmByTonnage || result.cfmByBTU} CFM`;
      case 'subcool':
        return `${result.subcool}°F`;
      case 'superheat':
        return `${result.superheat}°F`;
      case 'ductSize':
        return `${result.roundDiameter}" round`;
      default:
        return '';
    }
  };

  return (
    <div className="hvac-calculators">
      {/* Copy toast notification */}
      {copyToast && (
        <div className="copy-toast">
          {copyToast}
        </div>
      )}

      <div className="calculator-header">
        <h3>🧮 HVAC Calculators</h3>
        <p className="calculator-subtitle">Professional field calculation tools</p>
      </div>

      {/* Recent Calculations */}
      {recentCalculations.length > 0 && (
        <div className="recent-calculations">
          <h4 className="recent-title">Recent Calculations</h4>
          <div className="recent-list">
            {recentCalculations.map((calc) => (
              <div key={calc.id} className="recent-item">
                <span className="recent-type">{getCalculatorLabel(calc.type)}</span>
                <span className="recent-result">{getCalculatorSummary(calc)}</span>
                <span className="recent-time">{calc.timestamp}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="calculator-grid">
        <button
          className={`calculator-card ${activeCalculator === 'airflow' ? 'active' : ''}`}
          onClick={() => setActiveCalculator(activeCalculator === 'airflow' ? null : 'airflow')}
        >
          <span className="calculator-icon">💨</span>
          <span className="calculator-name">Airflow CFM</span>
        </button>

        <button
          className={`calculator-card ${activeCalculator === 'subcool' ? 'active' : ''}`}
          onClick={() => setActiveCalculator(activeCalculator === 'subcool' ? null : 'subcool')}
        >
          <span className="calculator-icon">❄️</span>
          <span className="calculator-name">Subcool</span>
        </button>

        <button
          className={`calculator-card ${activeCalculator === 'superheat' ? 'active' : ''}`}
          onClick={() => setActiveCalculator(activeCalculator === 'superheat' ? null : 'superheat')}
        >
          <span className="calculator-icon">🔥</span>
          <span className="calculator-name">Superheat</span>
        </button>

        <button
          className={`calculator-card ${activeCalculator === 'ductSize' ? 'active' : ''}`}
          onClick={() => setActiveCalculator(activeCalculator === 'ductSize' ? null : 'ductSize')}
        >
          <span className="calculator-icon">📐</span>
          <span className="calculator-name">Duct Sizing</span>
        </button>
      </div>

      {/* Airflow CFM Calculator */}
      {activeCalculator === 'airflow' && (
        <AirflowCalculator
          onCalculate={(inputs) => handleCalculate('airflow', inputs)}
          result={results.airflow}
          onCopy={copyToClipboard}
        />
      )}

      {/* Subcool Calculator */}
      {activeCalculator === 'subcool' && (
        <SubcoolCalculator
          onCalculate={(inputs) => handleCalculate('subcool', inputs)}
          result={results.subcool}
          onCopy={copyToClipboard}
        />
      )}

      {/* Superheat Calculator */}
      {activeCalculator === 'superheat' && (
        <SuperheatCalculator
          onCalculate={(inputs) => handleCalculate('superheat', inputs)}
          result={results.superheat}
          onCopy={copyToClipboard}
        />
      )}

      {/* Duct Sizing Calculator */}
      {activeCalculator === 'ductSize' && (
        <DuctSizeCalculator
          onCalculate={(inputs) => handleCalculate('ductSize', inputs)}
          result={results.ductSize}
          onCopy={copyToClipboard}
        />
      )}
    </div>
  );
};

// Sub-component for Airflow CFM Calculator
const AirflowCalculator = ({ onCalculate, result, onCopy }) => {
  const [tons, setTons] = useState('');
  const [btu, setBtu] = useState('');
  const [tempRise, setTempRise] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    const tonsVal = tons ? parseFloat(tons) : null;
    const btuVal = btu ? parseFloat(btu) : null;
    const tempRiseVal = tempRise ? parseFloat(tempRise) : null;

    if (!tonsVal && !btuVal) {
      setValidationError('Enter either Tonnage or BTU');
      return;
    }

    if (btuVal && !tempRiseVal) {
      setValidationError('Temp Rise required for BTU method');
      return;
    }

    // Range validation
    if (tonsVal && (tonsVal < 0.5 || tonsVal > 50)) {
      setValidationError('Tonnage must be between 0.5 and 50');
      return;
    }

    if (btuVal && (btuVal < 6000 || btuVal > 600000)) {
      setValidationError('BTU must be between 6,000 and 600,000');
      return;
    }

    if (tempRiseVal && (tempRiseVal < 5 || tempRiseVal > 50)) {
      setValidationError('Temp Rise must be between 5°F and 50°F');
      return;
    }

    setValidationError('');
    onCalculate({
      tons: tonsVal,
      btu: btuVal,
      tempRise: tempRiseVal
    });
  };

  return (
    <div className="calculator-form">
      <h4>Airflow CFM Calculator</h4>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Tonnage</label>
          <input
            type="number"
            step="0.5"
            min="0.5"
            max="50"
            placeholder="e.g., 3.5"
            value={tons}
            onChange={(e) => setTons(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>BTU/hour (optional)</label>
          <input
            type="number"
            step="1000"
            min="6000"
            max="600000"
            placeholder="e.g., 42000"
            value={btu}
            onChange={(e) => setBtu(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Temp Rise (°F) {btu && '- required'}</label>
          <input
            type="number"
            step="1"
            min="5"
            max="50"
            placeholder="e.g., 20"
            value={tempRise}
            onChange={(e) => setTempRise(e.target.value)}
          />
        </div>
        {validationError && (
          <div className="validation-error">{validationError}</div>
        )}
        <button type="submit" className="calc-submit">Calculate</button>
      </form>

      {result && (
        <div className="calc-result">
          {/* Show both results side-by-side if both methods were used */}
          {result.cfmByTonnage && result.cfmByBTU ? (
            <>
              <div className="result-comparison">
                <div className="result-method">
                  <div className="result-label">Tonnage Method:</div>
                  <div className="result-value">{result.cfmByTonnage} CFM</div>
                  <button
                    className="copy-result-btn"
                    onClick={() => onCopy(result.cfmByTonnage, 'CFM (Tonnage)')}
                  >
                    📋
                  </button>
                </div>
                <div className="result-method">
                  <div className="result-label">BTU Method:</div>
                  <div className="result-value">{result.cfmByBTU} CFM</div>
                  <button
                    className="copy-result-btn"
                    onClick={() => onCopy(result.cfmByBTU, 'CFM (BTU)')}
                  >
                    📋
                  </button>
                </div>
              </div>
              <div className="result-secondary">
                <span className="result-label">Difference:</span>
                <span className="result-value">{result.percentDiff}%</span>
              </div>
              {result.comparisonMessage && (
                <div className="result-recommendation">{result.comparisonMessage}</div>
              )}
            </>
          ) : (
            /* Single method result */
            <>
              <div className="result-primary">
                <span className="result-label">Calculated CFM:</span>
                <span className="result-value">{result.cfmByTonnage || result.cfmByBTU} CFM</span>
              </div>
              <button
                className="copy-result-btn-large"
                onClick={() => onCopy(result.cfmByTonnage || result.cfmByBTU, 'CFM')}
              >
                📋 Copy CFM
              </button>
            </>
          )}
          {result.recommendation && (
            <div className="result-recommendation">{result.recommendation}</div>
          )}
        </div>
      )}
    </div>
  );
};

// Sub-component for Subcool Calculator
const SubcoolCalculator = ({ onCalculate, result, onCopy }) => {
  const [liquidLineTemp, setLiquidLineTemp] = useState('');
  const [condensingTemp, setCondensingTemp] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onCalculate({ liquidLineTemp: parseFloat(liquidLineTemp), condensingTemp: parseFloat(condensingTemp) });
  };

  return (
    <div className="calculator-form">
      <h4>Subcool Calculator</h4>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Liquid Line (°F)</label>
          <input
            type="number"
            step="0.1"
            min="-50"
            max="200"
            placeholder="e.g., 95.5"
            value={liquidLineTemp}
            onChange={(e) => setLiquidLineTemp(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Condensing Temp (°F)</label>
          <input
            type="number"
            step="0.1"
            min="-50"
            max="200"
            placeholder="e.g., 110.0"
            value={condensingTemp}
            onChange={(e) => setCondensingTemp(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="calc-submit">Calculate</button>
      </form>

      {result && (
        <div className="calc-result">
          <div className="result-primary">
            <span className="result-label">Subcool:</span>
            <span className="result-value">{result.subcool}°F</span>
          </div>
          <button
            className="copy-result-btn-large"
            onClick={() => onCopy(result.subcool, 'Subcool')}
          >
            📋 Copy Result
          </button>
          <div className={`result-status ${result.status.includes('NORMAL') ? 'status-good' : 'status-warning'}`}>
            {result.status}
          </div>
          <div className="result-recommendation">{result.recommendation}</div>
        </div>
      )}
    </div>
  );
};

// Sub-component for Superheat Calculator
const SuperheatCalculator = ({ onCalculate, result, onCopy }) => {
  const [suctionLineTemp, setSuctionLineTemp] = useState('');
  const [evaporatingTemp, setEvaporatingTemp] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onCalculate({ suctionLineTemp: parseFloat(suctionLineTemp), evaporatingTemp: parseFloat(evaporatingTemp) });
  };

  return (
    <div className="calculator-form">
      <h4>Superheat Calculator</h4>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Suction Line (°F)</label>
          <input
            type="number"
            step="0.1"
            min="-50"
            max="200"
            placeholder="e.g., 55.0"
            value={suctionLineTemp}
            onChange={(e) => setSuctionLineTemp(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Evaporating Temp (°F)</label>
          <input
            type="number"
            step="0.1"
            min="-50"
            max="200"
            placeholder="e.g., 40.0"
            value={evaporatingTemp}
            onChange={(e) => setEvaporatingTemp(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="calc-submit">Calculate</button>
      </form>

      {result && (
        <div className="calc-result">
          <div className="result-primary">
            <span className="result-label">Superheat:</span>
            <span className="result-value">{result.superheat}°F</span>
          </div>
          <button
            className="copy-result-btn-large"
            onClick={() => onCopy(result.superheat, 'Superheat')}
          >
            📋 Copy Result
          </button>
          <div className={`result-status ${result.status.includes('NORMAL') ? 'status-good' : 'status-warning'}`}>
            {result.status}
          </div>
          <div className="result-recommendation">{result.recommendation}</div>
        </div>
      )}
    </div>
  );
};

// Sub-component for Duct Sizing Calculator
const DuctSizeCalculator = ({ onCalculate, result, onCopy }) => {
  const [cfm, setCfm] = useState('');
  const [velocity, setVelocity] = useState('700'); // Default 700 FPM

  const handleSubmit = (e) => {
    e.preventDefault();
    onCalculate({ cfm: parseFloat(cfm), velocity: parseFloat(velocity) });
  };

  return (
    <div className="calculator-form">
      <h4>Duct Sizing Calculator</h4>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Airflow (CFM)</label>
          <input
            type="number"
            step="1"
            min="50"
            max="50000"
            placeholder="e.g., 1400"
            value={cfm}
            onChange={(e) => setCfm(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Velocity (FPM)</label>
          <input
            type="number"
            step="50"
            min="300"
            max="2000"
            placeholder="700 (recommended)"
            value={velocity}
            onChange={(e) => setVelocity(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="calc-submit">Calculate</button>
      </form>

      {result && (
        <div className="calc-result">
          <div className="result-primary">
            <span className="result-label">Round Duct:</span>
            <span className="result-value">{result.roundDiameter}" diameter</span>
          </div>
          <button
            className="copy-result-btn-large"
            onClick={() => onCopy(`${result.roundDiameter}" round`, 'Duct Size')}
          >
            📋 Copy Round
          </button>
          <div className="result-secondary">
            <span className="result-label">Rectangular:</span>
            <span className="result-value">{result.rectangularWidth}" × {result.rectangularHeight}"</span>
            <button
              className="copy-result-btn"
              onClick={() => onCopy(`${result.rectangularWidth}" × ${result.rectangularHeight}"`, 'Rect Duct')}
            >
              📋
            </button>
          </div>
          <div className="result-secondary">
            <span className="result-label">Area:</span>
            <span className="result-value">{result.area} sq ft</span>
          </div>
          <div className="result-recommendation">{result.velocityStatus}</div>
        </div>
      )}
    </div>
  );
};

export default HVACCalculators;
